/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
/* eslint-disable complexity */
/* eslint-disable max-lines -- already at the budget pre-#469; splitting this file is a separate cleanup, not this fix's scope */

import { useContext, useEffect, useState } from 'react';

import { Axis, COUNTERTOP_DEFAULT_OVERHANG, Islands, Obstructions, type SkuAnnotation, SkuAnnotationType, nearestSixteenth, Vertex3 } from '@rta/rta-blueprint-library';

import { AppContext } from '@r-app/app-context';
import { DimensionLine } from '@r-components';
import { DIMENSION_FONT_SIZE, DisplayMode } from '@r-core';
import { RenderAllSkuLabelsOverhead } from '../render/render-all-sku-labels-overhead';
import { RenderCustomPanelsSideView } from '../render/render-custom-panels-side-view';
import { RenderIslandOverhead } from '../render/render-island-overhead';
import { renderTracking } from '@r-services/render-tracking/render-tracking-service';
import { showOverheadIslands } from '../show/show-overhead-islands';
import { showOverheadObstructions } from '../show/show-overhead-obstructions';
import { RenderSkuAnnotations, SkuAnnotationInteractionLayer, useSkuAnnotation } from '../sku-annotations';
import { transformToGlobal } from '../helpers/sku-labels';

export type DisplayOverheadModeProps = {
	islandIdFilter?: Array<string> | null;
};

export const DisplayOverheadMode = (props: DisplayOverheadModeProps = {}): React.ReactElement => {
	const { islandIdFilter = null } = props;
	const context = useContext(AppContext);
	const controller = context?.controller;

	const [rotation, setRotation] = useState<number>(controller?.viewport.config.rotation ?? 0);

	// Get annotation context
	const {
		activeTool,
		setActiveTool,
		selectedAnnotationId,
		setSelectedAnnotationId,
		setEditingAnnotationId
	} = useSkuAnnotation();

	useEffect(() => {
		if (controller) {
			setRotation(controller.viewport.config.rotation);
		}
	}, [controller?.viewport.config.rotation]);

	const isSkuActive = controller?.navigation.displayMode === DisplayMode.SkuOverhead || controller?.navigation.displayMode === DisplayMode.SkuOverheadManufacturing;
	const fontSizeFactor = controller?.options.dimensionFontSizeFactor ?? 1;

	// Auto-create or update island dimension annotation text boxes in SKU overhead mode.
	useEffect(() => {
		if (!controller || !isSkuActive) return;
		const { kitchen } = controller;
		const { skuAnnotations } = kitchen;
		const { islands: islandsCollection } = kitchen.design;

		// Compute text-box size (SVG units) to fit dimText at the given effective font size.
		// Matches render-sku-annotation foreignObject layout: ~1 svg-unit padding/side and 1.2 line-height.
		const computeDimTextBoxSize = (text: string): { width: number; height: number } => {
			const effectiveFontSize = DIMENSION_FONT_SIZE * fontSizeFactor;
			const lines = text.split('\n');
			const longestLineLength = lines.reduce((max, line) => Math.max(max, line.length), 0);
			const charWidthFactor = 0.6;
			const lineHeightFactor = 1.2;
			const paddingX = 2;
			const paddingY = 2;
			return {
				width: (longestLineLength * effectiveFontSize * charWidthFactor) + paddingX,
				height: (lines.length * effectiveFontSize * lineHeightFactor) + paddingY
			};
		};

		const syncAnnotation = (annotation: SkuAnnotation, dimText: string, fitSize: { width: number; height: number }): void => {
			if (annotation.text !== dimText) {
				annotation.text = dimText;
			}
			if (annotation.width !== fitSize.width || annotation.height !== fitSize.height) {
				const centerX = annotation.x + (annotation.width / 2);
				const centerY = annotation.y + (annotation.height / 2);
				annotation.width = fitSize.width;
				annotation.height = fitSize.height;
				annotation.x = centerX - (fitSize.width / 2);
				annotation.y = centerY - (fitSize.height / 2);
			}
		};

		const dimLabelPrefixes: Array<string> = [];

		islandsCollection.children.forEach((island) => {
			const { width, height, depth } = island.islandSize;
			const [islandName] = island.description.split(' | ');
			const dimLabelPrefix = `${islandName}\nLength `;
			dimLabelPrefixes.push(dimLabelPrefix);
			const dimText = `${dimLabelPrefix}${nearestSixteenth(width)}" | Height ${nearestSixteenth(height)}" | Depth ${nearestSixteenth(depth)}"`;
			const fitSize = computeDimTextBoxSize(dimText);

			// One annotation per island — keep the first linked one, delete any duplicates
			const [linked, ...duplicates] = skuAnnotations.children.filter((a) => a.linkedIslandId === island.id);
			duplicates.forEach((duplicate) => skuAnnotations.delete(duplicate.id));
			if (linked) {
				syncAnnotation(linked, dimText, fitSize);
				return;
			}

			// Compute island center in global SVG coordinates
			const global = transformToGlobal(0, 0, island.center.x, island.center.z, island.axis.y);

			// Adopt an unlinked annotation from a saved design (proximity first, then label text)
			const proximity = 5;
			const nearby = skuAnnotations.children.find((a) => {
				if (a.linkedIslandId) return false;
				const aCenterX = a.x + (a.width / 2);
				const aCenterY = a.y + (a.height / 2);
				return Math.abs(aCenterX - global.x) < proximity && Math.abs(aCenterY - global.z) < proximity;
			});
			const adopted = nearby ?? skuAnnotations.children.find((a) => !a.linkedIslandId && a.text.startsWith(dimLabelPrefix));
			if (adopted) {
				adopted.linkedIslandId = island.id;
				syncAnnotation(adopted, dimText, fitSize);
				return;
			}

			// Create new annotation
			const angle = island.axis.y;
			const labelRotation = (angle > 90 && angle < 270) ? angle + 180 : angle;

			skuAnnotations.add({
				type: SkuAnnotationType.TextBox,
				x: global.x - (fitSize.width / 2),
				y: global.z - (fitSize.height / 2),
				width: fitSize.width,
				height: fitSize.height,
				rotation: labelRotation,
				text: dimText,
				fontSize: DIMENSION_FONT_SIZE,
				color: '#333333',
				linkedIslandId: island.id
			});
		});

		// Purge leftover unlinked island labels (duplicates saved before island linking existed)
		skuAnnotations.children
			.filter((a) => !a.linkedIslandId && dimLabelPrefixes.some((prefix) => a.text.startsWith(prefix)))
			.forEach((leftover) => skuAnnotations.delete(leftover.id));
	}, [controller, isSkuActive, fontSizeFactor]);

	if (!controller) {
		return <></>;
	}

	const { options } = controller;
	const kitchenSize = controller.kitchen.getSize(controller.options.showCountertops);
	const { islands } = controller.kitchen.design;
	const { obstructions } = controller.kitchen.design;
	const scopedIslandChildren = islandIdFilter ? islands.children.filter((island) => islandIdFilter.includes(island.id)) : islands.children;

	let padding = { left: 3.7, top: 3, right: 3.7 };
	let showLeft = true;
	let showMiddle = true;
	let showRight = true;

	islands.children.forEach((island) => {
		const iAngle = island.axis.y;
		// const iD = island.id;
		const countertop = island.getCountertop();
		const hasBreakpoints = countertop ? countertop.breakpoints?.children?.length > 0 : false;
		const kitchenSizeDepthLeft = nearestSixteenth(kitchenSize.left.size.depth);
		const kitchenSizeDepthRight = nearestSixteenth(kitchenSize.right.size.depth);

		// console.log({ hasBreakpoints, option: options.showBreakpoints });
		if (hasBreakpoints && options.showBreakpoints) {
			padding = {
				left: 4.5,
				top: 4,
				right: 4.5
			}
		}

		if (iAngle === 270 || iAngle === 315) {
			padding.left += (((kitchenSizeDepthLeft.toString().length > 2 ? (kitchenSizeDepthLeft.toString().length - 3) / 3 : 0)));
		} else if (iAngle === 90 || iAngle === 45) {
			padding.right += (((kitchenSizeDepthRight.toString().length > 2 ? (kitchenSizeDepthRight.toString().length - 3) / 3 : 0)));
		}

		island.panels.children.forEach((panel) => {
			const overhangOverride = options.showCountertops ? undefined : COUNTERTOP_DEFAULT_OVERHANG;
			const { corners } = panel.getSidePoints({ includeCountertop: true, overhangOverride, translate: true, rotate: true });
			const length = corners[0].getDistance(corners[1]);
			const cornerLength = corners[2].getDistance(corners[3]);
			const panelAxis = panel.getOriginAngle();
			// console.log('island-corners:', kitchenSize);
			if (island.axis.y === 0) {
				if (length === kitchenSize.width) showMiddle = false;
				if (length === kitchenSize.right.size.depth) showRight = false;
				if (length === kitchenSize.left.size.depth) showLeft = false;
			} else if (island.axis.y === 90 && length === kitchenSize.right.size.depth) showRight = false;
			else if (island.axis.y === 270 && length === kitchenSize.left.size.depth) showLeft = false;
			else if ((island.axis.y === 45 || island.axis.y === 135) && (panelAxis !== 225) && nearestSixteenth(cornerLength) === nearestSixteenth(kitchenSize.right.size.depth)) showRight = false;
			else if ((island.axis.y === 225 || island.axis.y === 315) && (panelAxis !== 45) && nearestSixteenth(cornerLength) === nearestSixteenth(kitchenSize.left.size.depth)) showLeft = false;
		})
	});

	const dimensionLineOptions = {
		indicatorEnd: 'flat',
		indicatorStart: 'flat'
	};

	let useDimensionBox = true;

	const isSkuLayerActive = controller.navigation.displayMode === DisplayMode.SkuOverhead || controller.navigation.displayMode === DisplayMode.SkuOverheadManufacturing;

	switch (controller.navigation.displayMode) {
		case DisplayMode.OverheadManufacturing:
		case DisplayMode.SkuOverheadManufacturing:
			useDimensionBox = false;
			break;
		default:
			useDimensionBox = true;
			break;
	}

	renderTracking.incrementRender('DisplayOverhead');
	// 				translate(${-kitchenSize.origin.x}, -${kitchenSize.origin.z})

	const hasContent = islands.length > 0 || obstructions.length > 0;

	return controller.navigation !== null && hasContent
		? <>
			<g key={controller.kitchen.id}
				style={{ transformOrigin: '0 0' }}
				type="overhead"
				transform={`
					rotate(${rotation})
				`}>

				{/* Render obstructions first so they appear behind islands, dimension lines, and action bubbles */}
				{showOverheadObstructions(obstructions as Obstructions, controller)}
				{showOverheadIslands(islands as Islands, controller, islandIdFilter)}

				{/* Render SKU labels on top of all islands when in SKU Overhead mode */}
				{isSkuLayerActive && islands.length > 0 &&
					<RenderAllSkuLabelsOverhead islands={islands.children} />
				}

				{/* When SKU layer active, render dimensions on top of SKU labels */}
				{isSkuLayerActive && islands.children.map((island) => (
					<g key={`dims-top-${island.id}`}
						transform={`
							translate(${island.center.x}, ${island.center.z})
							rotate(${island.axis.y})
						`}>
						<RenderIslandOverhead islandId={island.id} renderDimensions="only" />
					</g>
				))}

				{/* SKU Annotations: Interaction layer for adding annotations (rendered first = below annotations) */}
				{isSkuLayerActive && (
					<SkuAnnotationInteractionLayer
						activeTool={activeTool}
						onAnnotationAdded={(id) => {
							setSelectedAnnotationId(id);
							// Auto-open text editor for new text boxes
							if (activeTool === 'textbox') {
								setEditingAnnotationId(id);
							}
							// Switch back to select tool after placing so user can interact with the new annotation
							setActiveTool('select');
						}}
					/>
				)}

				{/* SKU Annotations: Render annotations when in SKU mode (rendered last = on top, always clickable) */}
				{isSkuLayerActive && (
					<RenderSkuAnnotations
						activeTool={activeTool}
						selectedAnnotationId={selectedAnnotationId}
						onSelectAnnotation={(id) => {
							setSelectedAnnotationId(id);
						}}
						onEditAnnotation={(id) => {
							setEditingAnnotationId(id);
						}}
					/>
				)}

				{!islandIdFilter && controller.options.showOverheadDimensions && islands.length > 1 &&
				<>
					{showMiddle &&
					<DimensionLine {...dimensionLineOptions}
						persist={{ sourceId: 'overhead-overall', targetId: 'width' }}
						bBox={{
							origin: new Vertex3({ dataIn: { x: kitchenSize.origin.x, z: (kitchenSize.depth / -2) + kitchenSize.origin.z - padding.top }}),
							size: { width: kitchenSize.width, height: 0, depth: 0 }
						}}
						padding={-padding.top}
						length={kitchenSize.width}
						includeLabelBox={useDimensionBox}
						topView={true}
						canMove={true}
						dragConstraint="1d"
						parentRotation={rotation}
					/>
					}
					{showRight &&
					<DimensionLine {...dimensionLineOptions}
						persist={{ sourceId: 'overhead-overall', targetId: 'depth-right' }}
						bBox={{
							origin: new Vertex3({ dataIn: { x: kitchenSize.right.origin.x + (kitchenSize.right.size.width / 2) + padding.right, z: kitchenSize.right.origin.z }}),
							size: { width: kitchenSize.width, height: 0, depth: kitchenSize.right.size.depth }
						}}
						axis={new Axis({ dataIn: { y: 90 }})}
						padding={padding.right}
						length={kitchenSize.right.size.depth}
						includeLabelBox={useDimensionBox}
						topView={true}
						level={true}
						canMove={true}
						dragConstraint="1d"
						parentRotation={rotation}
					/>
					}
					{showLeft &&
					<DimensionLine {...dimensionLineOptions}
						persist={{ sourceId: 'overhead-overall', targetId: 'depth-left' }}
						bBox={{
							origin: new Vertex3({ dataIn: { x: kitchenSize.left.origin.x - (kitchenSize.left.size.width / 2) - padding.left, z: kitchenSize.left.origin.z }}),
							size: { width: kitchenSize.width, height: 0, depth: kitchenSize.left.size.depth }
						}}
						axis={new Axis({ dataIn: { y: 90 }})}
						padding={-padding.left}
						length={kitchenSize.left.size.depth}
						includeLabelBox={useDimensionBox}
						topView={true}
						level={true}
						canMove={true}
						dragConstraint="1d"
						parentRotation={rotation}
					/>
					}
				</>}

				{ /* developer mode : kitchen */ }
				{ controller.options.showDeveloperInsights && <>
					<circle cx={kitchenSize.left.origin.x} cy={kitchenSize.left.origin.z} r="1.25" fill="darkblue" opacity={0.5}/>
					<rect fill="none" strokeWidth={0.25} strokeDasharray="1 0.5 0.5" stroke="blue"
						x={kitchenSize.left.origin.x - (kitchenSize.left.size.width / 2)}
						y={kitchenSize.left.origin.z - (kitchenSize.left.size.depth / 2)}
						height={kitchenSize.left.size.depth} 	width={kitchenSize.left.size.width}
					/>

					<circle cx={kitchenSize.right.origin.x} cy={kitchenSize.right.origin.z} r="1.25" fill="darkblue" opacity={0.5}/>
					<rect fill="none" strokeWidth={0.25} strokeDasharray="1 0.5 0.5" stroke="blue"
						x={kitchenSize.right.origin.x - (kitchenSize.right.size.width / 2)}
						y={kitchenSize.right.origin.z - (kitchenSize.right.size.depth / 2)}
						height={kitchenSize.right.size.depth} 	width={kitchenSize.right.size.width}
					/>

					<circle cx={kitchenSize.origin.x} cy={kitchenSize.origin.z} r="1.25" fill="darkgreen" opacity={0.5}/>
					<rect fill="none" strokeWidth={0.25} strokeDasharray="1 0.5 0.5" stroke="green"
						x={kitchenSize.origin.x - (kitchenSize.width / 2)}
						y={kitchenSize.origin.z - (kitchenSize.depth / 2)}
						height={kitchenSize.depth} 	width={kitchenSize.width}
					/>

					<circle cx={0} cy={0} r="1.25" fill="darkblue" opacity={0.5}/>
				</>}

				{/* <circle cx={kitchenSize.origin.x} cy={kitchenSize.origin.z} r="1.25" fill="darkblue" opacity={0.5}/> */}
			</g>

			{/* Render panels with custom appliances/cutouts to the right side (outside rotation group) */}
			{scopedIslandChildren.length > 0 &&
				<RenderCustomPanelsSideView islands={scopedIslandChildren} kitchenWidth={kitchenSize.width} />
			}
		</>
		: null;
};
